import { type ReactNode, useMemo } from 'react';

import { SavedNoteOutcomesContext } from '@apps/web/components/phoebe_chat_view/artifacts/saved_note_outcomes_context';
import {
    type NoteOutcome,
    noteOutcomes,
} from '@apps/web/lib/agent_note_outcome_copy';
import { useAgentNotesForConversation } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/**
 * Resolves where this conversation's saved notes landed and publishes the
 * result to the note chips.
 *
 * Routing happens asynchronously after a turn ends, so this polls — but only
 * while the conversation is idle. Fetching during a live run would let chips
 * change underneath the scheduler while they are reading the reply, which is
 * the interruption the whole design avoids.
 *
 * Fetches for organizations armed with the agent-notes feature flag, and for
 * training-mode organizations (whose historical notes predate flag-only
 * arming). Everywhere else this never issues a request.
 *
 * Polling is the placeholder for a push we do not have yet. This component is
 * the only thing that fetches, so when the pipeline can publish "item applied"
 * to a subscriber, the swap is confined here: the context, the chips, and the
 * copy layer all keep reading the same shape.
 */
export function SavedNoteOutcomesProvider({
    organizationId,
    agentConversationId,
    agentRunId,
    isRunActive,
    isInTrainingMode,
    isAgentNotesArmed,
    children,
}: {
    readonly organizationId: string | null;
    readonly agentConversationId: string | null;
    readonly agentRunId: string | null;
    readonly isRunActive: boolean;
    readonly isInTrainingMode: boolean;
    readonly isAgentNotesArmed: boolean;
    readonly children: ReactNode;
}) {
    // Deliberately only the sidebar flag: chat runs mount save_note under
    // the sidebar arm alone, so an automations-only org has no chat notes
    // for this surface to render.
    const { data } = useAgentNotesForConversation({
        organizationId,
        agentConversationId,
        agentRunId,
        enabled: (isInTrainingMode || isAgentNotesArmed) && !isRunActive,
    });

    const value = useMemo(() => {
        const map = new Map<string, ReadonlyArray<NoteOutcome>>();
        for (const note of data?.notes ?? []) {
            const outcomes = noteOutcomes(note);
            if (outcomes.length > 0) {
                map.set(note.id, outcomes);
            }
        }
        return {
            notes: data?.notes ?? [],
            outcomesByNoteId: map,
        };
    }, [data]);

    return (
        <SavedNoteOutcomesContext.Provider value={value}>
            {children}
        </SavedNoteOutcomesContext.Provider>
    );
}
